import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  Accordion, Alert, Badge, Button, Card, Group, Loader, Modal, Stack, Text, TextInput, Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconLanguage, IconPlus, IconAlertTriangle, IconCheck,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchList } from '@/shared/api/jsonapi/client';
import { httpRequest } from '@/shared/api/client';
import { EmptyState } from '@/shared/components/EmptyState';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/admin/translate-structure')({ component: TranslateStructurePage });

interface TranslationRow extends Denormalized {
  type: 'translations';
  id: string;
  languageCode?: string;
  translationWeb?: string;
}

interface NewKeyForm {
  section: string;
  group: string;
  label: string;
  defaultIt: string;
  defaultEn: string;
}

const INITIAL: NewKeyForm = {
  section: 'general',
  group: '',
  label: '',
  defaultIt: '',
  defaultEn: '',
};

/**
 * Tipico schema interno (presente in Setup.languageSetup e in ogni Translation.translationWeb):
 *  {
 *    "general":   { "auth": { "login": "Accedi", ... }, "user": { ... } },
 *    "component": { ... },
 *    "template":  { ... }
 *  }
 * Visualizziamo type -> primaryRow -> { label: value } come accordion a 2 livelli.
 */
type TranslationTree = Record<string, Record<string, Record<string, unknown>>>;

function safeParse(json?: string): TranslationTree {
  if (!json) return {};
  try {
    const v = JSON.parse(json);
    return (typeof v === 'object' && v !== null ? v : {}) as TranslationTree;
  } catch {
    return {};
  }
}

function TranslateStructurePage() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['translations', 'all-for-structure'],
    queryFn: () => fetchList<TranslationRow>('translations', { page: { size: 50 } }),
  });

  const itRow = useMemo(() => data?.data.find((r) => r.languageCode === 'it'), [data]);
  const tree = useMemo<TranslationTree>(() => safeParse(itRow?.translationWeb), [itRow]);

  const [filter, setFilter] = useState('');
  const sections = useMemo(() => {
    const all = Object.entries(tree).map(([type, groups]) => ({
      type,
      groups: Object.entries(groups ?? {}).map(([group, labels]) => ({
        group,
        labels: Object.entries(labels ?? {}).map(([k, v]) => ({ key: k, value: typeof v === 'string' ? v : JSON.stringify(v) })),
      })),
    }));
    if (!filter.trim()) return all;
    const q = filter.trim().toLowerCase();
    return all
      .map((s) => ({
        ...s,
        groups: s.groups
          .map((g) => ({
            ...g,
            labels: g.labels.filter(
              (l) => l.key.toLowerCase().includes(q)
                || l.value.toLowerCase().includes(q)
                || g.group.toLowerCase().includes(q)
                || s.type.toLowerCase().includes(q),
            ),
          }))
          .filter((g) => g.labels.length > 0 || g.group.toLowerCase().includes(q)),
      }))
      .filter((s) => s.groups.length > 0 || s.type.toLowerCase().includes(q));
  }, [tree, filter]);

  const [modalOpened, modal] = useDisclosure(false);
  const form = useForm<NewKeyForm>({
    mode: 'controlled',
    initialValues: INITIAL,
    validate: {
      section: (v) => (v.trim() ? null : 'Sezione obbligatoria'),
      group: (v) => (v.trim() ? null : 'Gruppo obbligatorio'),
      label: (v) => (v.trim() ? null : 'Label obbligatoria'),
    },
  });

  const addKey = useMutation({
    mutationFn: async (v: NewKeyForm) => {
      // 1. addVoice crea la primary row se manca (label="")
      await httpRequest('/translations/addVoice', {
        method: 'POST',
        endpointType: 'plain',
        body: { type: v.section, primaryRow: v.group, label: '', value: '' },
      }).catch(() => {/* gia' esistente ok */});
      // 2. addVoice crea la label dentro la primary row
      await httpRequest('/translations/addVoice', {
        method: 'POST',
        endpointType: 'plain',
        body: { type: v.section, primaryRow: v.group, label: v.label, value: v.defaultIt },
      });
      // 3. patch IT con default IT (gia' settato sopra ma per sicurezza)
      // 4. patch EN: aggiorna la translation en se esiste
      if (data?.data) {
        const en = data.data.find((r) => r.languageCode === 'en');
        if (en && v.defaultEn) {
          // patchVoice opera sul languageSetup di Setup; per modificare la EN basta updateOne sulla Translation row.
          // Pattern alternativo: usare jsonapi PATCH /translations/{id} con il translationWeb modificato.
          const cur = safeParse(en.translationWeb);
          cur[v.section] ??= {};
          cur[v.section][v.group] ??= {};
          cur[v.section][v.group][v.label] = v.defaultEn;
          await httpRequest(`/api/translations/${en.id}`, {
            method: 'PATCH',
            body: {
              data: {
                type: 'translations',
                id: en.id,
                attributes: { translationWeb: JSON.stringify(cur) },
              },
            },
          });
        }
      }
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Chiave aggiunta' });
      qc.invalidateQueries({ queryKey: ['translations'] });
      modal.close();
      form.reset();
    },
    onError: (e) => notifications.show({ color: 'red', message: (e as Error).message || 'Errore' }),
  });

  return (
    <Stack>
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={1}>
            <IconLanguage size={26} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Struttura traduzioni
          </Title>
          <Text c="dimmed" size="sm">
            Chiavi raggruppate per <code>section.group.label</code>. Aggiungi una chiave e i suoi default IT / EN.
          </Text>
        </div>
        <Button leftSection={<IconPlus size={16} />} onClick={() => { form.setValues(INITIAL); modal.open(); }}>
          Nuova chiave
        </Button>
      </Group>

      <TextInput
        placeholder="Filtra per section / group / label / valore…"
        value={filter}
        onChange={(e) => setFilter(e.currentTarget.value)}
      />

      {isLoading && (
        <Card withBorder padding="lg" radius="md">
          <Group><Loader size="sm" /><Text>Carico traduzioni…</Text></Group>
        </Card>
      )}

      {isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          Errore di caricamento. Riprova.
        </Alert>
      )}

      {!isLoading && !isError && (
        <>
          {sections.length === 0 && (
            <Card withBorder padding="lg" radius="md">
              <EmptyState title="Nessuna chiave trovata" description="Prova ad allargare la ricerca o cambiare la lingua di filtro." py="md" />
            </Card>
          )}
          {sections.map((s) => (
            <Card key={s.type} withBorder padding={0} radius="md">
              <Group p="sm" justify="space-between">
                <Group>
                  <Badge variant="filled">{s.type}</Badge>
                  <Text size="sm" c="dimmed">{s.groups.length} gruppi</Text>
                </Group>
              </Group>
              <Accordion multiple variant="separated" radius={0} chevronPosition="left">
                {s.groups.map((g) => (
                  <Accordion.Item key={`${s.type}.${g.group}`} value={`${s.type}.${g.group}`}>
                    <Accordion.Control>
                      <Group>
                        <Text fw={600}>{g.group}</Text>
                        <Badge variant="light" size="sm">{g.labels.length}</Badge>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap={4}>
                        {g.labels.length === 0 && <Text c="dimmed" size="xs">Nessuna label.</Text>}
                        {g.labels.map((l) => (
                          <Group key={l.key} justify="space-between">
                            <Text size="sm" ff="monospace"><strong>{s.type}.{g.group}.</strong>{l.key}</Text>
                            <Text size="xs" c="dimmed" lineClamp={1} maw={400}>{l.value}</Text>
                          </Group>
                        ))}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Card>
          ))}
        </>
      )}

      <Modal
        opened={modalOpened}
        onClose={() => { modal.close(); form.reset(); }}
        title="Nuova chiave di traduzione"
        centered
        size="lg"
      >
        <form onSubmit={form.onSubmit((v) => addKey.mutate(v))}>
          <Stack>
            <Group grow>
              <TextInput
                label="Sezione (type)"
                description="Es. general, component, template"
                placeholder="general"
                required
                {...form.getInputProps('section')}
              />
              <TextInput
                label="Gruppo (primaryRow)"
                description="Es. auth, user, dashboard"
                placeholder="auth"
                required
                {...form.getInputProps('group')}
              />
            </Group>
            <TextInput
              label="Label"
              description="La chiave finale, es. login, profile"
              placeholder="login"
              required
              {...form.getInputProps('label')}
            />
            <Group grow>
              <TextInput
                label="Default IT"
                placeholder="Accedi"
                {...form.getInputProps('defaultIt')}
              />
              <TextInput
                label="Default EN"
                placeholder="Sign in"
                {...form.getInputProps('defaultEn')}
              />
            </Group>
            <Alert color="blue" icon={<IconCheck size={14} />}>
              Verra&apos; aggiunta la chiave <code>{form.values.section}.{form.values.group}.{form.values.label || '_'}</code> a tutte le lingue.
            </Alert>
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => { modal.close(); form.reset(); }}>
                Annulla
              </Button>
              <Button type="submit" loading={addKey.isPending}>
                Aggiungi
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
